// The composer's facing page — the chrome both wide-screen panels share.
//
// A dream opens into its structured fields; a waking entry opens into its pictures. They are the
// same object seen from two sides — the right-hand page of one book — so the heading, the collapse
// tab and the remembered open state live here rather than being written twice and drifting.
//
// Two things are deliberately *not* shared. Each panel keeps its own localStorage key: "hide the
// dream's fields" and "hide the pictures" are different preferences, and one of them answering for
// the other would surprise whoever set it. And each takes the surface of the card it faces —
// .weave for the dark dream card, .light for the near-white waking one — because a near-white page
// facing a near-black one does not read as one spread.

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { IconChevronLeft, IconChevronRight } from '../components/Icons';

/** Which card this panel is the facing page of. Matches the class on `.osheet-card`. */
export type CompanionSurface = 'weave' | 'light';

export interface CompanionPanelProps {
  /** DOM id, referenced by the collapse button's `aria-controls`. One per panel. */
  id: string;
  /** The panel's accessible name, shown as its heading. */
  title: string;
  /** localStorage key holding 'open' / 'closed'. Same shape as `oreia.chat.library`. */
  storageKey: string;
  /** What the collapsed tab is called — "Show dream details", "Show photos". */
  showLabel: string;
  hideLabel: string;
  surface: CompanionSurface;
  /** The card's chosen reading background, when there is one. See the call site. */
  tinted?: boolean;
  /**
   * Stand the panel down for as long as something else has the screen.
   *
   * Deliberately not the same thing as being collapsed: this leaves the remembered open/closed
   * preference alone, so the panel is back where the reader left it the moment the thing in front of
   * it closes. It also takes the reopen tab with it — a collapsed panel offers a way back in, and
   * offering one from behind a modal would be offering something that cannot be reached.
   */
  suppressed?: boolean;
  style?: CSSProperties;
  children: ReactNode;
}

/** Open unless the reader said otherwise: the panel is the point of the width. */
function openByDefault(key: string): boolean {
  return localStorage.getItem(key) !== 'closed';
}

export function CompanionPanel({
  id,
  title,
  storageKey,
  showLabel,
  hideLabel,
  surface,
  tinted = false,
  suppressed = false,
  style,
  children,
}: CompanionPanelProps) {
  const [open, setOpen] = useState(() => openByDefault(storageKey));

  useEffect(() => {
    localStorage.setItem(storageKey, open ? 'open' : 'closed');
  }, [storageKey, open]);

  const skin = `${surface}${tinted ? ' reader-tinted' : ''}`;

  if (suppressed) return null;

  if (!open) {
    return (
      <button
        className={`ocomp-reopen ${skin}`}
        style={style}
        onClick={() => setOpen(true)}
        aria-expanded={false}
        aria-label={showLabel}
      >
        <IconChevronLeft />
      </button>
    );
  }

  return (
    <aside className={`ocomp-side ${skin}`} style={style} id={id} aria-labelledby={`${id}-title`}>
      <div className="ocomp-side-hd">
        <h2 id={`${id}-title`}>{title}</h2>
        <button
          className="ocomp-collapse"
          onClick={() => setOpen(false)}
          aria-expanded
          aria-controls={id}
          aria-label={hideLabel}
        >
          <IconChevronRight />
        </button>
      </div>

      <div className="ocomp-side-body">{children}</div>
    </aside>
  );
}
